import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DiscountType } from '@travelity/api/generated';
import { type BaseSyntheticEvent, useCallback, useState } from 'react';
import { useForm } from '@travelity/form/use-form';
import { LoadingOverlay } from '@travelity/ui/loading-overlay';
import { useUpdatePackageDiscount } from '@travelity/api/services';
import AccordionButtons from '@/views/products/components/accordion-buttons';
import { useToast } from '@/hooks';
import type { PackageSidebarItemsProps } from '../types';
import { PackageFinancialsFields } from './PackageFinancialsFields';

type FinancialsFormValues = {
    type: DiscountType;
    amount: number;
};

const PackageFinancials = ({ product }: PackageSidebarItemsProps) => {
    const { t } = useTranslation('product');
    const { showSuccess } = useToast();
    const [isEditing, setIsEditing] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const { mutate: updatePackageDiscount, isLoading: isUpdatingFinancials } =
        useUpdatePackageDiscount();

    const {
        Form: FinancialsForm,
        reset: resetFinancials,
        formState: { errors: financialsErrors },
    } = useForm<FinancialsFormValues>({
        defaultValues: {
            type: product.discount?.type ?? DiscountType.ABSOLUTE,
            amount: product.discount?.amount ?? 0,
        },
        onSubmit: useCallback(
            (data: FinancialsFormValues, e?: BaseSyntheticEvent) => {
                e?.preventDefault();
                updatePackageDiscount(
                    {
                        productId: product.id,
                        data: {
                            type: data.type,
                            amount: data.amount,
                        },
                    },
                    {
                        onSuccess: () => {
                            setIsEditing(false);
                            showSuccess('UPDATE_PACKAGE_DISCOUNT_SUCCESS', {
                                name: product.name,
                            });
                        },
                    }
                );
            },
            [product.id, product.name, showSuccess, updatePackageDiscount]
        ),
        mode: 'onChange',
    });

    const onCancel = () => {
        resetFinancials();
        setIsEditing(false);
    };

    return (
        <FinancialsForm id="productFinancials">
            <Accordion
                disableGutters
                expanded={isExpanded}
                onChange={() => !isEditing && setIsExpanded(!isExpanded)}
            >
                <AccordionSummary>
                    {t('Pricing and Financials')}
                    {isExpanded && (
                        <AccordionButtons
                            editing={isEditing}
                            saving={isUpdatingFinancials}
                            errors={financialsErrors}
                            onEditCancel={onCancel}
                            onEdit={() => setIsEditing(true)}
                        />
                    )}
                </AccordionSummary>
                <AccordionDetails>
                    <PackageFinancialsFields
                        products={product.products}
                        isEditing={isEditing}
                    />
                </AccordionDetails>
                {isUpdatingFinancials && <LoadingOverlay />}
            </Accordion>
        </FinancialsForm>
    );
};

export default PackageFinancials;
